<template>
    <div
        ref="genredetailview"
        class="genre-detail"
        @scroll.passive="listenBottomOut"
    >
        <div class="genre-detail__backdrop" :style="backdropStyle"></div>
        <div class="genre-detail__shell">
            <header class="genre-detail__hero">
                <div class="genre-detail__hero-copy">
                    <div class="genre-detail__nav-row">
                        <button class="genre-detail__icon-button" type="button" @click="goBack">
                            <SvgIcon name="left-arrow" icon-class="genre-detail__icon-svg" />
                        </button>
                        <div class="genre-detail__crumb">{{ $tt('风格详情') }}</div>
                    </div>

                    <h1 class="genre-detail__title">{{ genreTitle }}</h1>
                    <p class="genre-detail__summary">{{ genreSummary }}</p>

                    <div class="genre-detail__actions">
                        <button class="genre-detail__primary-button" type="button" @click="playNow">
                            <SvgIcon name="player-play" icon-class="genre-detail__button-icon" />
                            <span>{{ $tt('播放歌曲') }}</span>
                        </button>
                        <button class="genre-detail__secondary-button" type="button" @click="shuffleNow">
                            <SvgIcon name="player-shuffle" icon-class="genre-detail__button-icon" />
                            <span>{{ $tt('随机播放') }}</span>
                        </button>
                    </div>
                </div>

                <aside class="genre-detail__visual-card">
                    <div class="genre-detail__cover-stage">
                        <div class="genre-detail__disc"></div>
                        <img :src="coverImage" :alt="genreTitle" class="genre-detail__cover">
                    </div>
                    <div class="genre-detail__stats">
                        <div class="genre-detail__stat">
                            <span>{{ $tt('专辑') }}</span>
                            <strong>{{ albumCount }}</strong>
                        </div>
                        <div class="genre-detail__stat">
                            <span>{{ $tt('歌曲') }}</span>
                            <strong>{{ trackCount }}</strong>
                        </div>
                        <div class="genre-detail__stat">
                            <span>{{ $tt('已载入') }}</span>
                            <strong>{{ loadedSummary }}</strong>
                        </div>
                    </div>
                </aside>
            </header>

            <div class="genre-detail__toolbar">
                <div class="genre-detail__tabs" role="tablist">
                    <button
                        v-for="{ value, text } in options"
                        :key="value"
                        :class="['genre-detail__tab', section === value ? 'is-active' : '']"
                        type="button"
                        role="tab"
                        :aria-selected="section === value ? 'true' : 'false'"
                        @click="handleChangeTab(value)"
                    >
                        {{ $tt(text) }}
                    </button>
                </div>

                <div v-if="isSuperuser" class="genre-detail__admin-actions">
                    <button class="genre-detail__admin-button" type="button" @click="openEditModal">
                        <SvgIcon name="edit" icon-class="genre-detail__admin-icon" />
                        <span>{{ $tt('编辑') }}</span>
                    </button>
                    <button class="genre-detail__danger-button" type="button" @click="openDeleteModal">
                        {{ $tt('删除') }}
                    </button>
                </div>
            </div>

            <main class="genre-detail__content">
                <section class="genre-detail__panel">
                    <div class="genre-detail__panel-header">
                        <div>
                            <h2 class="genre-detail__panel-title">
                                {{ section === 'albums' ? $tt('风格专辑') : $tt('风格歌曲') }}
                            </h2>
                            <p class="genre-detail__panel-subtitle">
                                {{ section === 'albums' ? albumPanelSubtitle : trackPanelSubtitle }}
                            </p>
                        </div>
                    </div>

                    <div v-if="isInitialLoading" class="genre-detail__state genre-detail__state--loading">
                        <span class="loading loading-spinner loading-lg"></span>
                        <span>{{ section === 'albums' ? $tt('正在加载专辑...') : $tt('正在加载歌曲...') }}</span>
                    </div>

                    <template v-else-if="section === 'tracks'">
                        <TrackList v-if="tracks.length" :tracks="tracks" :index-field="'index'" show-image />
                        <div v-else class="genre-detail__state">
                            <div class="genre-detail__empty-mark">
                                <SvgIcon name="playlist" icon-class="genre-detail__empty-icon" />
                            </div>
                            <h3>{{ $tt('暂无歌曲') }}</h3>
                            <p>{{ $tt('这个风格下还没有可播放的歌曲。') }}</p>
                        </div>
                    </template>

                    <template v-else>
                        <AlbumList v-if="albums.length" :items="albums" />
                        <div v-else class="genre-detail__state">
                            <div class="genre-detail__empty-mark">
                                <SvgIcon name="icon-image" icon-class="genre-detail__empty-icon" />
                            </div>
                            <h3>{{ $tt('暂无专辑') }}</h3>
                            <p>{{ $tt('这个风格下还没有收录专辑。') }}</p>
                        </div>
                    </template>

                    <div v-if="isLoadingMore" class="genre-detail__loadmore">
                        <span class="loading loading-spinner loading-md"></span>
                        <span>{{ $tt('继续加载...') }}</span>
                    </div>
                </section>
            </main>
        </div>

        <dialog ref="editModal" class="modal modal-bottom sm:modal-middle">
            <div class="modal-box genre-detail__modal">
                <div class="genre-detail__modal-header">
                    <div>
                        <div class="genre-detail__modal-label">{{ $tt('编辑风格') }}</div>
                        <h3 class="genre-detail__modal-title">{{ genreTitle }}</h3>
                    </div>
                    <form method="dialog">
                        <button class="genre-detail__modal-close" type="submit">
                            <SvgIcon name="x" icon-class="genre-detail__close-icon" />
                        </button>
                    </form>
                </div>

                <div class="genre-detail__form-grid">
                    <div class="genre-detail__field">
                        <label>{{ $tt('风格名称') }}</label>
                        <input
                            type="text"
                            :value="id"
                            disabled
                            class="input input-bordered input-sm w-full"
                        >
                    </div>

                    <div class="genre-detail__upload-card">
                        <label>{{ $tt('风格封面') }}</label>
                        <div class="genre-detail__upload-preview" v-if="reloadImg">
                            <img :src="adminPreviewImage" :alt="genreTitle">
                            <button
                                v-if="genreImagePreview || currentGenreImage"
                                class="genre-detail__upload-clear"
                                type="button"
                                @click="removeGenreImage"
                            >
                                <SvgIcon name="x" icon-class="genre-detail__clear-icon" />
                            </button>
                            <input
                                ref="genreImageInput"
                                type="file"
                                accept="image/*"
                                class="genre-detail__file-input"
                                :disabled="isUploadingGenreImage"
                                @change="handleGenreImageChange"
                            >
                            <div v-if="isUploadingGenreImage" class="genre-detail__uploading">
                                <span class="loading loading-spinner loading-md"></span>
                                <span>{{ $tt('上传中...') }}</span>
                            </div>
                        </div>
                        <div class="genre-detail__upload-actions">
                            <button class="genre-detail__secondary-button" type="button" @click="triggerGenreImageReplacement">
                                {{ $tt('上传图片') }}
                            </button>
                            <button
                                v-if="albums.length"
                                class="genre-detail__secondary-button"
                                type="button"
                                @click="handleCopy('genre_img', albums[0].image)"
                            >
                                {{ $tt('使用首张专辑封面') }}
                            </button>
                        </div>
                    </div>
                </div>

                <div v-if="albums.length" class="genre-detail__recommend">
                    <div class="genre-detail__modal-label">{{ $tt('封面推荐') }}</div>
                    <div class="genre-detail__cover-grid">
                        <button
                            v-for="item in albums.slice(0, 8)"
                            :key="item.id || item.name"
                            class="genre-detail__cover-option"
                            type="button"
                            @click="handleCopy('genre_img', item.image)"
                        >
                            <img :src="item.image || '/static/dist/img/album_null.jpg'" :alt="item.name">
                            <span>{{ item.name }}</span>
                        </button>
                    </div>
                </div>

                <div class="modal-action genre-detail__modal-actions">
                    <form method="dialog">
                        <button class="genre-detail__secondary-button" type="submit">{{ $tt('取消') }}</button>
                    </form>
                    <button class="genre-detail__primary-button" type="button" @click="handleSave">
                        {{ $tt('保存修改') }}
                    </button>
                </div>
            </div>
            <form method="dialog" class="modal-backdrop">
                <button>{{ $tt('关闭') }}</button>
            </form>
        </dialog>

        <dialog ref="deleteModal" class="modal modal-bottom sm:modal-middle">
            <div class="modal-box genre-detail__modal genre-detail__modal--danger">
                <div class="genre-detail__modal-header">
                    <div>
                        <div class="genre-detail__modal-label">{{ $tt('删除风格') }}</div>
                        <h3 class="genre-detail__modal-title">{{ genreTitle }}</h3>
                    </div>
                </div>
                <div class="genre-detail__delete-copy">
                    <p>{{ $tt('删除音乐: 将风格从音乐收藏中移除，同时删除该风格下的音乐文件。') }}</p>
                    <p>{{ $tt('删除收藏: 将风格从音乐收藏中移除，但并不会删除音乐文件。') }}</p>
                </div>
                <div class="modal-action genre-detail__modal-actions">
                    <form method="dialog">
                        <button class="genre-detail__secondary-button" type="submit">{{ $tt('取消') }}</button>
                    </form>
                    <button class="genre-detail__danger-button" type="button" @click="handleDelete('delete_track')">
                        {{ $tt('删除收藏') }}
                    </button>
                    <button class="genre-detail__danger-solid-button" type="button" @click="handleDelete('delete_file')">
                        {{ $tt('删除音乐') }}
                    </button>
                </div>
            </div>
            <form method="dialog" class="modal-backdrop">
                <button>{{ $tt('关闭') }}</button>
            </form>
        </dialog>

        <button
            v-if="curScrollTop > 700"
            class="genre-detail__to-top"
            type="button"
            @click="scrollToTop"
        >
            <SvgIcon name="chevron-up" icon-class="genre-detail__to-top-icon" />
        </button>
    </div>
</template>

<script>
    import AlbumList from '@/views/library/album/AlbumList.vue'
    import TrackList from '@/views/library/track/TrackList.vue'
    import SvgIcon from '@/components/SvgIcon.vue'
    import { mapGetters } from 'vuex'

    const PAGE_SIZE = 36

    export default {
        name: 'genre',
        components: {
            AlbumList,
            TrackList,
            SvgIcon
        },
        data() {
            return {
                tracks: [],
                albums: [],
                id: this.$route.params.id,
                section: 'albums',
                genreInfo: null,
                currentGenreImage: '',
                genreImagePreview: '',
                loading: false,
                offset: 0,
                hasMore: true,
                loading2: false,
                offset2: 0,
                hasMore2: true,
                curScrollTop: 0,
                options: [
                    { text: '专辑', value: 'albums' },
                    { text: '歌曲', value: 'tracks' }
                ],
                formData: {
                    genre_img: ''
                },
                uploadHeader: [
                    { name: 'X-CSRFToken', value: this.getCookie('django_vue_cli_csrftoken') },
                    { name: 'AUTHORIZATION', value: this.getCookie('AUTHORIZATION') }
                ],
                files1: [],
                uploadUrl: '/apimt/upload_image/',
                artistsData: [],
                isUploadingGenreImage: false,
                reloadImg: true
            }
        },
        computed: {
            ...mapGetters({
                isSuperuser: 'getIsSuperuser'
            }),
            genreTitle() {
                return this.genreInfo?.name || this.id
            },
            albumCount() {
                return Number(this.genreInfo?.albumCount || 0) || this.albums.length
            },
            trackCount() {
                return Number(this.genreInfo?.trackCount || 0) || this.tracks.length
            },
            coverImage() {
                return this.genreImagePreview
                    || this.currentGenreImage
                    || (this.albums[0] && this.albums[0].image)
                    || (this.tracks[0] && this.tracks[0].image)
                    || '/static/dist/img/album_null.jpg'
            },
            adminPreviewImage() {
                return this.genreImagePreview || this.currentGenreImage || this.coverImage
            },
            backdropStyle() {
                return {
                    backgroundImage: `url('${this.coverImage}')`
                }
            },
            loadedSummary() {
                return `${this.albums.length} / ${this.tracks.length}`
            },
            genreSummary() {
                if (!this.albumCount && !this.trackCount) {
                    return this.$tt('这个风格还没有可播放内容。')
                }
                if (this.albumCount && this.trackCount) {
                    return this.$tt('收拢 {albumCount} 张专辑和 {trackCount} 首歌曲，适合按同一种气质连续聆听。', {
                        albumCount: this.albumCount,
                        trackCount: this.trackCount
                    })
                }
                if (this.trackCount) {
                    return this.$tt('已整理 {trackCount} 首歌曲，可以直接播放或随机听。', {
                        trackCount: this.trackCount
                    })
                }
                return this.$tt('已整理 {albumCount} 张专辑，可以从封面开始挑选。', {
                    albumCount: this.albumCount
                })
            },
            albumPanelSubtitle() {
                return this.$tt('{count} 张专辑已载入', { count: this.albums.length })
            },
            trackPanelSubtitle() {
                return this.$tt('{count} 首歌曲已载入', { count: this.tracks.length })
            },
            isInitialLoading() {
                if (this.section === 'albums') {
                    return this.loading && !this.albums.length
                }
                return this.loading2 && !this.tracks.length
            },
            isLoadingMore() {
                if (this.section === 'albums') {
                    return this.loading && !!this.albums.length
                }
                return this.loading2 && !!this.tracks.length
            }
        },
        watch: {
            '$route.params.id': {
                immediate: false,
                async handler(value) {
                    this.id = value
                    await this.initializePage()
                }
            }
        },
        async created() {
            await this.initializePage()
        },
        activated() {
            this.$nextTick(() => {
                if (this.$refs.genredetailview) {
                    this.$refs.genredetailview.scrollTop = this.curScrollTop || 0
                }
            })
        },
        methods: {
            async initializePage() {
                this.section = 'albums'
                this.tracks = []
                this.albums = []
                this.genreInfo = null
                this.currentGenreImage = ''
                this.genreImagePreview = ''
                this.formData.genre_img = ''
                this.loading = false
                this.offset = 0
                this.hasMore = true
                this.loading2 = false
                this.offset2 = 0
                this.hasMore2 = true
                this.curScrollTop = 0

                if (this.$refs.genredetailview) {
                    this.$refs.genredetailview.scrollTop = 0
                }

                await Promise.all([
                    this.loadGenreMeta(),
                    this.loadMore('albums'),
                    this.loadMore('tracks')
                ])
            },
            async loadGenreMeta() {
                try {
                    const items = await this.$api.subsonic.getGenres('most-tracks')
                    const target = items.find(item => String(item.id) === String(this.id) || String(item.name) === String(this.id))
                    if (target) {
                        this.genreInfo = target
                        this.currentGenreImage = target.image || ''
                    }
                } catch (error) {
                    console.log(error)
                }
            },
            loadAlbums(offset) {
                return this.$api.subsonic.getAlbumsByGenre(this.id, PAGE_SIZE, offset)
            },
            loadTracks(offset) {
                return this.$api.subsonic.getTracksByGenre(this.id, PAGE_SIZE, offset)
            },
            async loadMore(targetSection = this.section) {
                if (targetSection === 'tracks') {
                    if (this.loading2 || !this.hasMore2) {
                        return
                    }
                    this.loading2 = true
                    try {
                        const tracks = await this.loadTracks(this.offset2)
                        this.tracks.push(...tracks)
                        this.offset2 += tracks.length
                        this.hasMore2 = tracks.length === PAGE_SIZE
                    } finally {
                        this.loading2 = false
                    }
                    return
                }

                if (this.loading || !this.hasMore) {
                    return
                }
                this.loading = true
                try {
                    const albums = await this.loadAlbums(this.offset)
                    this.albums.push(...albums)
                    this.offset += albums.length
                    this.hasMore = albums.length === PAGE_SIZE
                } finally {
                    this.loading = false
                }
            },
            async ensureTracksForPlayback() {
                if (this.tracks.length) {
                    return this.tracks
                }
                const tracks = await this.$api.subsonic.getTracksByGenre(this.id, 100, 0)
                this.tracks = tracks
                this.offset2 = tracks.length
                this.hasMore2 = tracks.length === 100
                return tracks
            },
            async playNow() {
                const tracks = await this.ensureTracksForPlayback()
                if (!tracks.length) {
                    return this.$myMsg.notify({
                        title: this.$tt('暂无歌曲'),
                        type: 'warning',
                        time: 2000,
                    })
                }
                return this.$store.dispatch('playTrackList', {
                    tracks
                })
            },
            async shuffleNow() {
                const tracks = await this.ensureTracksForPlayback()
                if (!tracks.length) {
                    return this.$myMsg.notify({
                        title: this.$tt('暂无歌曲'),
                        type: 'warning',
                        time: 2000,
                    })
                }
                return this.$store.dispatch('player/shuffleNow', {
                    tracks
                })
            },
            async handleChangeTab(value) {
                this.section = value
                if (value === 'albums' && !this.albums.length && this.hasMore) {
                    await this.loadMore('albums')
                }
                if (value === 'tracks' && !this.tracks.length && this.hasMore2) {
                    await this.loadMore('tracks')
                }
            },
            goBack() {
                if (window.history.length > 1) {
                    this.$router.back()
                    return
                }
                this.$router.push({ path: '/genres/a-z/' })
            },
            openEditModal() {
                if (this.$refs.editModal) {
                    this.$refs.editModal.showModal()
                }
            },
            openDeleteModal() {
                if (this.$refs.deleteModal) {
                    this.$refs.deleteModal.showModal()
                }
            },
            handleEdit() {
                this.openEditModal()
            },
            handleDelete(mode) {
                this.$api.Task.deleteGenre({ id: this.id, mode }).then((res) => {
                    if (res.result) {
                        if (this.$refs.deleteModal) {
                            this.$refs.deleteModal.close()
                        }
                        this.$myMsg.notify({
                            title: this.$tt('删除成功！'),
                            type: 'success',
                            time: 2000,
                        })
                        this.$router.push({ path: '/genres/a-z/' })
                    } else {
                        this.$myMsg.notify({
                            title: this.$tt('删除失败！'),
                            type: 'error',
                            time: 2000,
                        })
                    }
                })
            },
            listenBottomOut() {
                const scroller = this.$refs.genredetailview
                if (!scroller) {
                    return
                }

                const scrollTop = scroller.scrollTop
                const scrollHeight = scroller.scrollHeight
                const clientHeight = scroller.clientHeight
                this.curScrollTop = scrollTop

                if (scrollTop + clientHeight < scrollHeight - 80) {
                    return
                }

                if (this.section === 'albums') {
                    this.loadMore('albums')
                } else {
                    this.loadMore('tracks')
                }
            },
            handleRes(response) {
                if (response.result) {
                    this.formData.genre_img = response.data
                    return true
                }
                return false
            },
            handleSave() {
                this.$api.Task.postGenre({
                    id: this.id,
                    genre_img: this.formData.genre_img,
                }).then((res) => {
                    if (res.result) {
                        this.currentGenreImage = this.formData.genre_img || this.genreImagePreview
                        this.genreImagePreview = ''
                        this.$myMsg.notify({
                            title: this.$tt('修改成功！'),
                            type: 'success',
                            time: 2000,
                        })
                        if (this.$refs.editModal) {
                            this.$refs.editModal.close()
                        }
                    } else {
                        this.$myMsg.notify({
                            title: this.$tt('修改失败！'),
                            type: 'error',
                            time: 2000,
                        })
                    }
                })
            },
            handleCopy(key, value) {
                if (key !== 'genre_img') {
                    return
                }
                this.files1 = [
                    {
                        name: 'cover.png',
                        status: 'done',
                        url: value
                    }
                ]
                this.formData.genre_img = value
                this.genreImagePreview = value
            },
            scrollToTop() {
                if (this.$refs.genredetailview) {
                    this.$refs.genredetailview.scrollTo({ top: 0, behavior: 'smooth' })
                }
            },
            removeGenreImage() {
                this.genreImagePreview = ''
                this.currentGenreImage = ''
                this.formData.genre_img = ''
                if (this.$refs.genreImageInput) {
                    this.$refs.genreImageInput.value = ''
                }
            },
            triggerGenreImageReplacement() {
                this.createTempFileInput('genre')
            },
            createTempFileInput(type) {
                const input = document.createElement('input')
                input.type = 'file'
                input.accept = 'image/*'
                input.style.display = 'none'
                input.onchange = (event) => {
                    if (type === 'genre') {
                        this.handleGenreImageChange(event)
                    }
                    if (input.parentNode) {
                        input.parentNode.removeChild(input)
                    }
                }
                document.body.appendChild(input)
                input.click()
            },
            handleGenreImageChange(event) {
                const file = event.target.files && event.target.files[0]
                if (!file) return

                if (!file.type.startsWith('image/')) {
                    this.$myMsg.notify({
                        title: this.$tt('文件类型错误'),
                        content: this.$tt('请选择图片文件'),
                        type: 'error',
                        time: 2000,
                    })
                    return
                }

                if (file.size > 5 * 1024 * 1024) {
                    this.$myMsg.notify({
                        title: this.$tt('文件过大'),
                        content: this.$tt('图片大小不能超过5MB'),
                        type: 'error',
                        time: 2000,
                    })
                    return
                }

                const reader = new FileReader()
                reader.onload = (e) => {
                    this.genreImagePreview = e.target.result
                }
                reader.readAsDataURL(file)
                this.uploadImage(file, 'genre')
            },
            uploadImage(file, type) {
                const formData = new FormData()
                formData.append('upload_file', file)

                if (type === 'genre') {
                    this.isUploadingGenreImage = true
                }

                const xhr = new XMLHttpRequest()
                xhr.open('POST', this.uploadUrl, true)
                xhr.setRequestHeader('X-CSRFToken', this.getCookie('django_vue_cli_csrftoken'))
                xhr.setRequestHeader('AUTHORIZATION', this.getCookie('AUTHORIZATION'))
                xhr.withCredentials = true

                xhr.onload = () => {
                    if (type === 'genre') {
                        this.isUploadingGenreImage = false
                    }

                    if (xhr.status === 200) {
                        try {
                            const response = JSON.parse(xhr.responseText)
                            if (this.handleRes(response)) {
                                this.$myMsg.notify({
                                    title: this.$tt('上传成功'),
                                    type: 'success',
                                    time: 2000,
                                })
                            } else {
                                this.$myMsg.notify({
                                    title: this.$tt('上传失败'),
                                    content: response.message || this.$tt('未知错误'),
                                    type: 'error',
                                    time: 2000,
                                })
                            }
                        } catch (error) {
                            console.log(error)
                            this.$myMsg.notify({
                                title: this.$tt('上传失败'),
                                content: this.$tt('服务器响应格式错误'),
                                type: 'error',
                                time: 2000,
                            })
                        }
                    } else {
                        this.$myMsg.notify({
                            title: this.$tt('上传失败'),
                            content: this.$tt('HTTP错误: {status}', { status: xhr.status }),
                            type: 'error',
                            time: 2000,
                        })
                    }
                }

                xhr.onerror = () => {
                    if (type === 'genre') {
                        this.isUploadingGenreImage = false
                    }
                    this.$myMsg.notify({
                        title: this.$tt('上传失败'),
                        content: this.$tt('网络错误'),
                        type: 'error',
                        time: 2000,
                    })
                }

                xhr.send(formData)
            }
        }
    }
</script>

<style scoped>
.genre-detail {
    position: relative;
    height: calc(100dvh - 70px);
    overflow-y: auto;
    overflow-x: hidden;
    color: oklch(var(--bc));
    background:
        radial-gradient(circle at 12% 0%, color-mix(in oklch, oklch(var(--p)) 16%, transparent), transparent 28rem),
        radial-gradient(circle at 90% 10%, color-mix(in oklch, oklch(var(--a)) 10%, transparent), transparent 24rem),
        oklch(var(--b1));
}

.genre-detail__backdrop {
    position: absolute;
    inset: 0;
    pointer-events: none;
    background-size: cover;
    background-position: center;
    opacity: 0.08;
    filter: blur(48px) saturate(1.18);
    transform: scale(1.06);
}

.genre-detail__shell {
    position: relative;
    z-index: 1;
    width: min(100%, 1380px);
    margin: 0 auto;
    padding: 1.25rem clamp(1rem, 2.5vw, 2.2rem) 3rem;
}

.genre-detail__hero {
    min-height: 22rem;
    display: grid;
    grid-template-columns: minmax(0, 1.35fr) minmax(20rem, 0.65fr);
    gap: clamp(1.2rem, 3vw, 3rem);
    align-items: stretch;
}

.genre-detail__hero-copy,
.genre-detail__visual-card,
.genre-detail__panel,
.genre-detail__modal {
    border: 1px solid color-mix(in oklch, oklch(var(--bc)) 9%, transparent);
    background: color-mix(in oklch, oklch(var(--b1)) 88%, transparent);
    box-shadow: 0 24px 70px color-mix(in oklch, oklch(var(--bc)) 10%, transparent);
    backdrop-filter: blur(18px);
}

.genre-detail__hero-copy {
    min-width: 0;
    border-radius: 1.5rem;
    padding: clamp(1.35rem, 3vw, 2.4rem);
    display: flex;
    flex-direction: column;
    justify-content: space-between;
}

.genre-detail__nav-row,
.genre-detail__actions,
.genre-detail__toolbar,
.genre-detail__tabs,
.genre-detail__admin-actions,
.genre-detail__panel-header,
.genre-detail__modal-header,
.genre-detail__modal-actions,
.genre-detail__upload-actions,
.genre-detail__loadmore {
    display: flex;
    align-items: center;
}

.genre-detail__nav-row,
.genre-detail__actions,
.genre-detail__admin-actions,
.genre-detail__modal-actions,
.genre-detail__upload-actions {
    gap: 0.7rem;
    flex-wrap: wrap;
}

.genre-detail__nav-row {
    margin-bottom: 2.2rem;
}

.genre-detail__icon-button,
.genre-detail__modal-close,
.genre-detail__to-top,
.genre-detail__upload-clear {
    border: 0;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.genre-detail__icon-button,
.genre-detail__modal-close {
    width: 2.65rem;
    height: 2.65rem;
    background: color-mix(in oklch, oklch(var(--b2)) 82%, transparent);
    color: oklch(var(--bc));
}

.genre-detail__icon-svg,
.genre-detail__close-icon,
.genre-detail__to-top-icon,
.genre-detail__clear-icon {
    width: 1rem;
    height: 1rem;
}

::v-deep .genre-detail__icon-svg path,
::v-deep .genre-detail__admin-icon path,
::v-deep .genre-detail__close-icon path,
::v-deep .genre-detail__to-top-icon path,
::v-deep .genre-detail__clear-icon path {
    fill: currentColor;
}

.genre-detail__crumb,
.genre-detail__modal-label,
.genre-detail__field label,
.genre-detail__upload-card label {
    font-size: 0.82rem;
    font-weight: 800;
    color: color-mix(in oklch, oklch(var(--bc)) 58%, transparent);
    letter-spacing: 0.02em;
}

.genre-detail__title {
    max-width: 13ch;
    margin: 0;
    font-size: clamp(3rem, 8vw, 6.8rem);
    line-height: 0.95;
    font-weight: 900;
    letter-spacing: -0.04em;
    word-break: break-word;
}

.genre-detail__summary {
    max-width: 58ch;
    margin: 1.1rem 0 0;
    color: color-mix(in oklch, oklch(var(--bc)) 66%, transparent);
    font-size: 1rem;
    line-height: 1.75;
}

.genre-detail__actions {
    margin-top: 2rem;
}

.genre-detail__primary-button,
.genre-detail__secondary-button,
.genre-detail__admin-button,
.genre-detail__danger-button,
.genre-detail__danger-solid-button {
    min-height: 2.9rem;
    border: 0;
    border-radius: 999px;
    padding: 0 1.15rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    font-size: 0.9rem;
    font-weight: 800;
    white-space: nowrap;
    transition: transform 0.22s ease, background 0.22s ease, box-shadow 0.22s ease;
}

.genre-detail__primary-button:active,
.genre-detail__secondary-button:active,
.genre-detail__admin-button:active,
.genre-detail__danger-button:active,
.genre-detail__danger-solid-button:active,
.genre-detail__tab:active,
.genre-detail__cover-option:active {
    transform: translateY(1px) scale(0.99);
}

.genre-detail__primary-button {
    background: color-mix(in oklch, oklch(var(--p)) 72%, oklch(var(--bc)) 28%);
    color: oklch(var(--pc));
    box-shadow: 0 18px 38px color-mix(in oklch, oklch(var(--p)) 20%, transparent);
}

.genre-detail__secondary-button,
.genre-detail__admin-button {
    background: color-mix(in oklch, oklch(var(--b2)) 86%, transparent);
    color: oklch(var(--bc));
}

.genre-detail__danger-button {
    background: color-mix(in oklch, oklch(var(--er)) 12%, transparent);
    color: color-mix(in oklch, oklch(var(--er)) 82%, oklch(var(--bc)) 18%);
}

.genre-detail__danger-solid-button {
    background: oklch(var(--er));
    color: oklch(var(--erc));
}

.genre-detail__button-icon {
    width: 1rem;
    height: 1rem;
    fill: currentColor;
}

.genre-detail__admin-icon {
    width: 0.95rem;
    height: 0.95rem;
}

.genre-detail__visual-card {
    border-radius: 1.5rem;
    padding: 1rem;
    min-width: 0;
    display: grid;
    grid-template-rows: minmax(0, 1fr) auto;
    gap: 1rem;
}

.genre-detail__cover-stage {
    position: relative;
    min-height: 18rem;
    display: flex;
    align-items: center;
    justify-content: center;
}

.genre-detail__disc {
    position: absolute;
    width: min(72%, 16rem);
    aspect-ratio: 1 / 1;
    right: 6%;
    border-radius: 999px;
    background:
        radial-gradient(circle at center, oklch(var(--b1)) 0 9%, color-mix(in oklch, oklch(var(--bc)) 92%, oklch(var(--b1)) 8%) 9% 15%, color-mix(in oklch, oklch(var(--bc)) 78%, oklch(var(--b1)) 22%) 15% 100%);
    box-shadow: 0 20px 52px color-mix(in oklch, oklch(var(--bc)) 18%, transparent);
}

.genre-detail__cover {
    position: relative;
    z-index: 1;
    width: min(74%, 18rem);
    aspect-ratio: 1 / 1;
    object-fit: cover;
    border-radius: 1.25rem;
    box-shadow: 0 24px 64px color-mix(in oklch, oklch(var(--bc)) 18%, transparent);
}

.genre-detail__stats {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.6rem;
}

.genre-detail__stat {
    border-radius: 1rem;
    padding: 0.9rem;
    background: color-mix(in oklch, oklch(var(--b2)) 72%, transparent);
}

.genre-detail__stat span {
    display: block;
    color: color-mix(in oklch, oklch(var(--bc)) 54%, transparent);
    font-size: 0.78rem;
    font-weight: 700;
}

.genre-detail__stat strong {
    display: block;
    margin-top: 0.25rem;
    font-size: 1.35rem;
    line-height: 1;
    font-weight: 900;
}

.genre-detail__toolbar {
    margin-top: 1rem;
    justify-content: space-between;
    gap: 1rem;
}

.genre-detail__tabs {
    width: fit-content;
    padding: 0.35rem;
    border-radius: 999px;
    background: color-mix(in oklch, oklch(var(--b2)) 82%, transparent);
    gap: 0.25rem;
}

.genre-detail__tab {
    min-width: 5.8rem;
    min-height: 2.65rem;
    border: 0;
    border-radius: 999px;
    background: transparent;
    color: color-mix(in oklch, oklch(var(--bc)) 62%, transparent);
    font-weight: 800;
}

.genre-detail__tab.is-active {
    background: color-mix(in oklch, oklch(var(--bc)) 88%, oklch(var(--b1)) 12%);
    color: oklch(var(--b1));
    box-shadow: 0 12px 26px color-mix(in oklch, oklch(var(--bc)) 12%, transparent);
}

.genre-detail__content {
    margin-top: 1rem;
}

.genre-detail__panel {
    border-radius: 1.4rem;
    padding: clamp(1rem, 2vw, 1.5rem);
}

.genre-detail__panel-header {
    justify-content: space-between;
    margin-bottom: 1rem;
}

.genre-detail__panel-title {
    margin: 0;
    font-size: 1.35rem;
    font-weight: 900;
    letter-spacing: -0.02em;
}

.genre-detail__panel-subtitle {
    margin: 0.35rem 0 0;
    color: color-mix(in oklch, oklch(var(--bc)) 55%, transparent);
    font-size: 0.9rem;
}

.genre-detail__state {
    min-height: 16rem;
    border-radius: 1.2rem;
    background: color-mix(in oklch, oklch(var(--b2)) 58%, transparent);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.65rem;
    text-align: center;
    color: color-mix(in oklch, oklch(var(--bc)) 60%, transparent);
}

.genre-detail__state h3 {
    margin: 0;
    font-size: 1.15rem;
    color: oklch(var(--bc));
}

.genre-detail__state p {
    margin: 0;
}

.genre-detail__state--loading {
    flex-direction: row;
}

.genre-detail__empty-mark {
    width: 4rem;
    height: 4rem;
    border-radius: 1.1rem;
    background: color-mix(in oklch, oklch(var(--b1)) 88%, transparent);
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.genre-detail__empty-icon {
    width: 1.8rem;
    height: 1.8rem;
    fill: currentColor;
}

.genre-detail__loadmore {
    justify-content: center;
    gap: 0.55rem;
    padding: 1rem 0 0;
    color: color-mix(in oklch, oklch(var(--bc)) 55%, transparent);
}

.genre-detail__modal {
    border-radius: 1.4rem;
    background: oklch(var(--b1));
    color: oklch(var(--bc));
}

.genre-detail__modal-header {
    justify-content: space-between;
    gap: 1rem;
}

.genre-detail__modal-title {
    margin: 0.2rem 0 0;
    font-size: 1.35rem;
    font-weight: 900;
}

.genre-detail__form-grid {
    margin-top: 1.1rem;
    display: grid;
    grid-template-columns: minmax(0, 0.82fr) minmax(0, 1.18fr);
    gap: 1rem;
}

.genre-detail__field,
.genre-detail__upload-card {
    display: grid;
    align-content: start;
    gap: 0.55rem;
}

.genre-detail__upload-preview {
    position: relative;
    aspect-ratio: 1 / 1;
    border-radius: 1.1rem;
    overflow: hidden;
    background: color-mix(in oklch, oklch(var(--b2)) 82%, transparent);
}

.genre-detail__upload-preview img,
.genre-detail__cover-option img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.genre-detail__file-input {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
}

.genre-detail__upload-clear {
    position: absolute;
    top: 0.7rem;
    right: 0.7rem;
    z-index: 2;
    width: 2.2rem;
    height: 2.2rem;
    background: color-mix(in oklch, oklch(var(--bc)) 82%, transparent);
    color: oklch(var(--b1));
}

.genre-detail__uploading {
    position: absolute;
    inset: 0;
    z-index: 3;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    background: color-mix(in oklch, oklch(var(--b1)) 82%, transparent);
}

.genre-detail__recommend {
    margin-top: 1.1rem;
}

.genre-detail__cover-grid {
    margin-top: 0.7rem;
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0.75rem;
}

.genre-detail__cover-option {
    border: 0;
    border-radius: 1rem;
    padding: 0.55rem;
    background: color-mix(in oklch, oklch(var(--b2)) 72%, transparent);
    color: oklch(var(--bc));
    text-align: left;
}

.genre-detail__cover-option img {
    aspect-ratio: 1 / 1;
    border-radius: 0.8rem;
}

.genre-detail__cover-option span {
    display: block;
    margin-top: 0.45rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.78rem;
    color: color-mix(in oklch, oklch(var(--bc)) 68%, transparent);
}

.genre-detail__delete-copy {
    margin-top: 1rem;
    display: grid;
    gap: 0.65rem;
    color: color-mix(in oklch, oklch(var(--bc)) 66%, transparent);
    line-height: 1.7;
}

.genre-detail__to-top {
    position: fixed;
    right: 1.4rem;
    bottom: 2rem;
    z-index: 5;
    width: 3rem;
    height: 3rem;
    background: color-mix(in oklch, oklch(var(--bc)) 88%, oklch(var(--b1)) 12%);
    color: oklch(var(--b1));
    box-shadow: 0 18px 36px color-mix(in oklch, oklch(var(--bc)) 18%, transparent);
}

@media (prefers-reduced-motion: no-preference) {
    .genre-detail__hero-copy,
    .genre-detail__visual-card,
    .genre-detail__panel {
        animation: genreDetailEnter 0.5s ease both;
    }

    .genre-detail__visual-card {
        animation-delay: 0.06s;
    }

    .genre-detail__panel {
        animation-delay: 0.1s;
    }
}

@keyframes genreDetailEnter {
    from {
        opacity: 0;
        transform: translateY(14px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@media (max-width: 1024px) {
    .genre-detail {
        height: calc(100dvh - 60px);
    }

    .genre-detail__hero {
        grid-template-columns: 1fr;
    }

    .genre-detail__cover-stage {
        min-height: 16rem;
    }

    .genre-detail__form-grid {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 768px) {
    .genre-detail__shell {
        padding: 0.9rem 0.8rem 2rem;
    }

    .genre-detail__hero-copy,
    .genre-detail__visual-card,
    .genre-detail__panel {
        border-radius: 1.25rem;
    }

    .genre-detail__title {
        max-width: 100%;
        font-size: clamp(2.4rem, 14vw, 4rem);
    }

    .genre-detail__summary {
        font-size: 0.94rem;
    }

    .genre-detail__toolbar {
        align-items: stretch;
        flex-direction: column;
    }

    .genre-detail__tabs,
    .genre-detail__admin-actions {
        width: 100%;
    }

    .genre-detail__tab,
    .genre-detail__admin-button,
    .genre-detail__danger-button {
        flex: 1;
    }

    .genre-detail__stats,
    .genre-detail__cover-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .genre-detail__actions,
    .genre-detail__upload-actions,
    .genre-detail__modal-actions {
        flex-direction: column;
        align-items: stretch;
    }

    .genre-detail__primary-button,
    .genre-detail__secondary-button,
    .genre-detail__danger-button,
    .genre-detail__danger-solid-button {
        width: 100%;
    }
}
</style>
